import React, { useContext, memo, useMemo, useState } from 'react'
import type { Genre, TransformedImageId } from '../../../grab/types'
import { Card, NavigationLink } from './components-lib'
import { StyleSheet } from '../stylesheet'
import { ResourceImage } from './resource-image'
import type { AppPath } from '../navigation'
import type { StaticEvent, StaticGroup, TileLayoutConfig, TileSlotPosition, TileFieldType, TileSlotConfig } from '../types'
import { MainConfig, PageScheduleConfig, useLang } from '../chapito-static'
import { formatDayLong, formatEndAndStartHour, nowMs, showHour } from '../../../services/date'

// Default layout for events: TITLE at bottom center
export const DEFAULT_EVENT_TILE_LAYOUT: TileLayoutConfig = {
  slots: [{ position: 'BOTTOM_CENTER', field: 'TITLE' }],
}

// Default layout for guests: NAME at bottom center
export const DEFAULT_GUEST_TILE_LAYOUT: TileLayoutConfig = {
  slots: [{ position: 'BOTTOM_CENTER', field: 'NAME' }],
}

type TileCardProps = {
  event?: StaticEvent
  guest?: StaticGroup
  tileLayout?: TileLayoutConfig
  navLink?: AppPath
  style?: React.CSSProperties
}

export const TileCard: React.FC<TileCardProps> = memo(function TileCard({ event, guest, tileLayout, navLink, style }) {
  const lang = useLang()
  const config = useContext(MainConfig)
  const schedule = useContext(PageScheduleConfig)
  const dateFormatConfig = { lang, timezone: config.timezone }
  const [isHovered, setHovered] = useState(false)

  // Determine which layout to use
  const layout = tileLayout ?? (event ? DEFAULT_EVENT_TILE_LAYOUT : DEFAULT_GUEST_TILE_LAYOUT)

  // Get image to display (shown as background unless disabled in layout config)
  const backgroundImageEnabled = layout.showBackgroundImage !== false
  const image: TransformedImageId | undefined = backgroundImageEnabled ? event?.image ?? guest?.image : undefined

  // Find the next upcoming event for a guest
  const nextEvent = useMemo(
    () => guest?.relatedEvents?.filter((e) => e.showStartDate > nowMs()).sort((a, b) => a.showStartDate - b.showStartDate)[0] ?? null,
    [guest?.relatedEvents]
  )

  // Get field value based on field type
  const getFieldValue = (field: TileFieldType, tagCategoryName?: string): React.ReactNode => {
    switch (field) {
      case 'TITLE':
        return event?.title ?? null

      case 'NAME':
        return guest?.name?.toLocaleUpperCase(lang) ?? null

      case 'TIME': {
        if (!event) return null
        const timeStr = showHour(event.showStartDate, schedule, nowMs()) ? formatEndAndStartHour(event, dateFormatConfig, schedule) : ''
        return timeStr || null
      }

      case 'TAGS': {
        let genres: Genre[] = event?.genres ?? guest?.genres ?? []
        // Filter by category if specified
        if (tagCategoryName && genres.length > 0) {
          genres = genres.filter((g) => g.category?.name === tagCategoryName)
        }
        return genres.length > 0 ? genres.map((g) => g.name).join(', ') : null
      }

      case 'DESCRIPTION': {
        const desc = event?.description ?? guest?.description ?? null
        if (!desc) return null
        // Strip HTML tags for tile display (limited space)
        const tmp = document.createElement('div')
        tmp.innerHTML = desc
        return tmp.textContent ?? tmp.innerText ?? desc
      }

      case 'DATE_TIME': {
        if (!event) return null
        const dateStr = capitalizeDateString(formatDayLong(event.day, dateFormatConfig))
        const timeStr = showHour(event.showStartDate, schedule, nowMs()) ? formatEndAndStartHour(event, dateFormatConfig, schedule) : ''
        return `${dateStr}${timeStr ? ` ${timeStr}` : ''}`
      }

      case 'SCENE':
        return event?.scene?.name && !event.hideLocation && !config.eventsHideLocation ? event.scene.name : null

      case 'NEXT_EVENT_DATE_TIME': {
        if (!nextEvent) return null
        const dateStr = capitalizeDateString(formatDayLong(nextEvent.day, dateFormatConfig))
        const timeStr = showHour(nextEvent.showStartDate, schedule, nowMs()) ? formatEndAndStartHour(nextEvent, dateFormatConfig, schedule) : ''
        return `${dateStr}${timeStr ? ` ${timeStr}` : ''}`
      }

      case 'NEXT_EVENT_DATE': {
        if (!nextEvent) return null
        return capitalizeDateString(formatDayLong(nextEvent.day, dateFormatConfig))
      }

      case 'NEXT_EVENT_SCENE':
        return nextEvent?.scene?.name && !nextEvent.hideLocation && !config.eventsHideLocation ? nextEvent.scene.name : null

      case 'NONE':
      default:
        return null
    }
  }

  // Apply layout-level styles
  const cardContainerStyle: React.CSSProperties = {
    ...styles.container,
    ...style,
    // Drop the default Card colorBlockBackground: tiles render their own
    // image / fallback color, and a non-transparent backdrop here would leak
    // through whenever the user picks a transparent fallback color.
    backgroundColor: 'transparent',
    aspectRatio: 1,
    transform: isHovered ? 'scale(1.05) rotate(2deg)' : 'scale(1)',
    zIndex: isHovered ? 10 : 0,
    transition: 'all .3s',
  }

  // Group slots by row and resolve values
  type RowName = 'TOP' | 'MIDDLE' | 'BOTTOM'
  type ColName = 'LEFT' | 'CENTER' | 'RIGHT'

  const getRowName = (position: TileSlotPosition): RowName => {
    if (position.startsWith('TOP')) return 'TOP'
    if (position.startsWith('MIDDLE')) return 'MIDDLE'
    return 'BOTTOM'
  }

  const getColName = (position: TileSlotPosition): ColName => {
    if (position.endsWith('LEFT')) return 'LEFT'
    if (position.endsWith('CENTER')) return 'CENTER'
    return 'RIGHT'
  }

  type Cell = { slot: TileSlotConfig; col: ColName; value: React.ReactNode } | null
  const rows = (() => {
    // Always emit fixed [LEFT, CENTER, RIGHT] cells per row so columns stay
    // visually aligned regardless of the order in which the user added slots.
    const rowMap: Record<RowName, Cell[]> = {
      TOP: [null, null, null],
      MIDDLE: [null, null, null],
      BOTTOM: [null, null, null],
    }
    const colIndex: Record<ColName, number> = { LEFT: 0, CENTER: 1, RIGHT: 2 }

    for (const slot of layout.slots) {
      if (slot.field === 'NONE') continue
      const value = getFieldValue(slot.field, slot.tagCategoryName ?? undefined)
      if (value === null) continue
      const row = getRowName(slot.position)
      const col = getColName(slot.position)
      rowMap[row][colIndex[col]] = { slot, col, value }
    }

    return rowMap
  })()

  const getTextAlign = (col: ColName): React.CSSProperties['textAlign'] => {
    if (col === 'LEFT') return 'left'
    if (col === 'CENTER') return 'center'
    return 'right'
  }

  const getJustify = (col: ColName): string => {
    if (col === 'LEFT') return 'flex-start'
    if (col === 'CENTER') return 'center'
    return 'flex-end'
  }

  const renderRow = (rowName: RowName): React.ReactNode => {
    const items = rows[rowName]
    const filledCount = items.reduce((acc, c) => acc + (c ? 1 : 0), 0)
    if (filledCount === 0) return null

    const rowStyle: React.CSSProperties = {
      position: 'absolute',
      left: 0,
      right: 0,
      display: 'flex',
      flexWrap: 'nowrap',
      ...(rowName === 'TOP' ? { top: 0 } : rowName === 'BOTTOM' ? { bottom: 0 } : { top: '50%', transform: 'translateY(-50%)' }),
    }

    // If only one slot is set on this row, let it span the full width
    // (justification still honors its column). Otherwise use a fixed
    // 3-column grid so column positions stay stable across edits.
    const singleCell = filledCount === 1

    // Anchor each cell's content within the row so that, when sibling cells
    // wrap on multiple lines, the shorter ones (and their backgrounds) keep
    // their natural height instead of stretching to the row's height.
    const rowAlignItems: React.CSSProperties['alignItems'] = rowName === 'TOP' ? 'flex-start' : rowName === 'BOTTOM' ? 'flex-end' : 'center'

    return (
      <div style={rowStyle}>
        {items.map((cell, index) => {
          if (singleCell && !cell) return null

          const cellStyle: React.CSSProperties = {
            flex: singleCell ? '1 1 100%' : '1 1 33.3333%',
            maxWidth: singleCell ? '100%' : '33.3333%',
            display: 'flex',
            alignItems: rowAlignItems,
            justifyContent: cell ? getJustify(cell.col) : 'center',
            padding: '8px',
            minWidth: 0,
            overflow: 'hidden',
          }

          if (!cell) {
            return <div key={index} style={cellStyle} />
          }

          const { slot, col, value } = cell
          // Treat fully-transparent (#rrggbb00) as "no background block":
          // skip the padded/blurred chip entirely so the user actually sees
          // through the slot.
          const hasBackground = !!slot.backgroundColor && !isFullyTransparent(slot.backgroundColor)

          const textStyle: React.CSSProperties = {
            color: slot.font?.color ?? 'white',
            fontSize: slot.font?.size ? `${slot.font.size}px` : '1em',
            fontFamily: slot.font?.family,
            fontWeight: slot.font?.weight,
            textAlign: getTextAlign(col),
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            ...(hasBackground
              ? {
                  backgroundColor: slot.backgroundColor,
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  borderRadius: '6px',
                  padding: '4px 10px',
                }
              : {}),
          }

          return (
            <div key={index} style={cellStyle}>
              <span style={textStyle}>{value}</span>
            </div>
          )
        })}
      </div>
    )
  }

  const noImageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    backgroundColor: layout.fallbackColor ?? '#667eea',
  }

  const cardContent = (
    <Card style={cardContainerStyle}>
      <div style={styles.fullImageContainer}>
        {image ? <ResourceImage imageId={image} style={styles.image} alt="" /> : <div style={noImageStyle} />}
        {image && <div style={styles.dropShadow} />}
        {renderRow('TOP')}
        {renderRow('MIDDLE')}
        {renderRow('BOTTOM')}
      </div>
    </Card>
  )

  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      {navLink ? <NavigationLink path={navLink}>{cardContent}</NavigationLink> : cardContent}
    </div>
  )
})

// Returns true for #rrggbb00 — the only hex form that means "fully see-through"
// in the BO ColorPicker (hexa mode). Other lengths/formats are treated as
// non-transparent.
function isFullyTransparent(color: string): boolean {
  return /^#[0-9a-f]{6}00$/i.test(color)
}

function capitalizeDateString(text: string): string {
  return text
    .split(' ')
    .map((part) => (part.length > 0 ? part[0].toUpperCase() + part.substring(1) : ''))
    .join(' ')
}

const styles = StyleSheet.create({
  container: {
    padding: 0,
    overflow: 'hidden',
  },

  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  fullImageContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  dropShadow: {
    background: 'linear-gradient(0deg, rgba(0,0,0,0.595098107602416) 18%, rgba(255,255,255,0) 100%)',
    height: '30%',
    width: '100%',
    position: 'absolute',
    bottom: 0,
  },
})
